import React from 'react'
import { useTranslation } from 'react-i18next'
import { applyAppLocale, getAppLocale } from '../i18n'
import type { AppLocale } from '../i18n/localeStorage'
import { savePreferredLocale } from '../api/preferredLocale'
import { useAuth } from '../contexts/AuthContext'

type Props = {
  /** Show compact English | Español control (login). */
  compact?: boolean
  className?: string
  /** When true, skip profile RPC (pre-login). */
  localOnly?: boolean
}

/** English / Español language control. Profile RPC when signed in; always caches locally. */
export function LanguageToggle({ compact = false, className = '', localOnly = false }: Props) {
  const { t } = useTranslation(['common', 'settings'])
  const { user, refreshProfile } = useAuth()
  const [busy, setBusy] = React.useState(false)
  const [notice, setNotice] = React.useState<string | null>(null)
  const current = getAppLocale()

  const select = async (locale: AppLocale) => {
    if (locale === current || busy) return
    setBusy(true)
    setNotice(null)
    try {
      await applyAppLocale(locale)
      if (!localOnly && user) {
        try {
          await savePreferredLocale(locale)
          await refreshProfile()
          setNotice(t('common:language.saved'))
        } catch {
          setNotice(t('common:language.saveFailed'))
        }
      }
    } finally {
      setBusy(false)
    }
  }

  if (compact) {
    return (
      <div className={`flex items-center justify-center gap-2 text-sm ${className}`}>
        <button
          type="button"
          disabled={busy}
          onClick={() => void select('en')}
          className={`min-h-[40px] px-3 rounded-lg font-medium transition-colors ${
            current === 'en'
              ? 'bg-blue-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          {t('common:language.english')}
        </button>
        <span className="text-gray-300" aria-hidden>
          |
        </span>
        <button
          type="button"
          disabled={busy}
          onClick={() => void select('es')}
          className={`min-h-[40px] px-3 rounded-lg font-medium transition-colors ${
            current === 'es'
              ? 'bg-blue-600 text-white'
              : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          {t('common:language.spanish')}
        </button>
      </div>
    )
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <p className="text-sm font-medium text-gray-700">{t('common:language.label')}</p>
      <div className="flex flex-wrap gap-2">
        {(['en', 'es'] as AppLocale[]).map((locale) => (
          <button
            key={locale}
            type="button"
            disabled={busy}
            onClick={() => void select(locale)}
            className={`min-h-[44px] px-4 rounded-xl border text-sm font-semibold transition-colors ${
              current === locale
                ? 'bg-blue-50 border-blue-200 text-blue-700'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            }`}
          >
            {locale === 'en' ? t('common:language.english') : t('common:language.spanish')}
          </button>
        ))}
      </div>
      {notice && <p className="text-xs text-gray-500">{notice}</p>}
    </div>
  )
}
